<template>
  <div
    class="operation-report-show-body-form-item flex flex-col"
    :class="{
      required: required,
      disabled,
      'is-editing': isEditing,
      'has-errors': hasErrors,
    }"
  >
    <div
      class="form-item-label aisea-text-16-700-dark-gray"
      :class="{
        required: required,
        disabled,
        'is-editing': isEditing,
        'has-errors': hasErrors,
      }"
    >
      {{ label }}
    </div>
    <!-- 画像がある時 -->
    <div
      class="form-item-value-images flex flex-wrap row-gap-4"
      v-if="hasImage"
    >
      <div
        :id="valueFile.id"
        class="image-container vs-con-loading__container relative"
        v-for="valueFile in valueFiles"
        :key="valueFile.hash"
      >
        <div
          class="image-container-loading w-full h-full"
          v-if="valueFile.loading"
        ></div>
        <div
          class="image-container-success w-full h-full"
          v-else-if="valueFile.success"
        >
          <img :src="valueFile.url" :alt="valueFile.name" class="image" />
          <div class="image-container-header">
            <vs-dropdown
              :color="AISEA_COLOR.BLUE"
              class="dropdown-list cursor-pointer"
              vs-trigger-click
            >
              <feather-icon
                class="action-button-icon"
                svgClasses="h-6 w-6"
                icon="MoreVerticalIcon"
              ></feather-icon>
              <vs-dropdown-menu class="content-dropdown-menu-operation">
                <vs-dropdown-item
                  v-for="{ event, onClick, label, icon } in eventList"
                  :key="event"
                  @click="() => onClick(valueFile)"
                >
                  <span class="flex items-center">
                    <feather-icon
                      :icon="icon"
                      svgClasses="h-4 w-4"
                      class="mr-2"
                    />
                    <span>{{ label }}</span>
                  </span>
                </vs-dropdown-item>
              </vs-dropdown-menu>
            </vs-dropdown>
          </div>
        </div>
        <error-message :imageReadErrorMessage="imageReadErrorMessage" v-else />
      </div>
      <!-- プレビュー用 -->
      <light-box
        v-if="hasImage"
        ref="lightbox"
        :media="images"
        :show-light-box="false"
        :show-thumbs="false"
        :show-caption="false"
        :disableScroll="false"
      >
      </light-box>
    </div>
    <!-- 画像がない時 -->
    <template v-else>
      <!-- 詳細 -->
      <div
        class="form-item-value aisea-text-16-400-dark-gray"
        v-if="!isEditing"
      >
        {{ fallbackValue }}
      </div>
      <!-- 編集 -->
      <template v-else>
        <div class="image-select-button-wrapper">
          <!-- 画像選択ボタン -->
          <image-select-button @click="showUploadDialog" :disabled="disabled" />
          <!-- 画像選択ダイアログ -->
          <image-uploader-dialog
            :active="isActiveUploadDialog"
            @close="closeUploadDialog"
            @cancel="closeUploadDialog"
            @accept="onUpload"
          />
        </div>
      </template>
    </template>
    <!-- nullable label -->
    <template v-if="isEditing">
      <input
        type="hidden"
        :name="name"
        :value="valueForValidate"
        v-validate="vValidate"
      />
      <div
        class="nullable-label flex items-center aisea-text-16-400-dark-gray"
        v-if="hasNullableLabel"
      >
        <vs-checkbox
          v-model="nullableChecked"
          :color="nullableCheckBoxColor"
          :disabled="disabled"
          @change="onChange"
        >
          <span>{{ nullableLabel }}</span>
        </vs-checkbox>
      </div>
    </template>
  </div>
</template>

<script>
// 以下に対応したコンポーネント
//   INPUT_TYPES.SINGLE_IMAGE_FILE, // 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード)
import LightBox from "vue-image-lightbox";
import ErrorMessage from "./ErrorMessage";
import ImageSelectButton from "./ImageSelectButton";
import ImageUploaderDialog from "./ImageUploaderDialog.vue";
import { ColorMixin } from "@/mixins";
const { AISEA_COLOR } = ColorMixin.data();
import isString from "lodash/isString";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

const EVENTS = {
  PREVIEW: "preview",
  DOWNLOAD: "download",
  DELETE: "delete",
  CHANGE: "change",
  UPLOAD: "upload",
  SHOW_DIALOG: "show-dialog",
  CLOSE_DIALOG: "close-dialog",
};

export default {
  name: "OperationReportShowBodyFormItemImage",
  components: {
    LightBox,
    ErrorMessage,
    ImageSelectButton,
    ImageUploaderDialog,
  },
  props: {
    label: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array, // {hash: string; url: string; success: boolean; loading: boolean}[]
      default: () => [],
    },
    fallbackValue: {
      type: String,
      default: "ー",
    },
    // 編集中かどうか
    isEditing: {
      type: Boolean,
      default: false,
    },
    nullableLabel: {
      type: String,
      required: false,
    },
    isNullableChecked: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      required: true,
    },
    isActiveUploadDialog: {
      type: Boolean,
      default: false,
    },
    hasReportFormVersionItemValue: {
      type: Boolean,
      default: false,
    },
    defaultValue: {
      type: Array,
      required: false,
    },
  },

  data() {
    return {
      nullableChecked: false,
      isDefaultValueInserted: false, // default値を挿入したかどうか -> defaultValueの更新が無限ループになるのを防ぐのに使う
    };
  },

  watch: {
    value: {
      handler() {
        const {
          hasReportFormVersionItemValue,
          defaultValue,
          onChangeByDefaultValue,
        } = this;
        if (
          !hasReportFormVersionItemValue &&
          !isNil(defaultValue) &&
          Array.isArray(defaultValue) &&
          defaultValue.length > 0
        ) {
          if (this.isDefaultValueInserted) return;
          onChangeByDefaultValue(); // デフォルト値を使用する場合に、そのまま更新リクエストできるようにする
        }
        this.isDefaultValueInserted = true;
      },
      immediate: true,
    },
    valueFiles: {
      handler() {
        this.$nextTick(() => {
          this.valueFiles.forEach(valueFile => {
            if (valueFile.loading) {
              this.$vs.loading({
                type: "border",
                container: `#${valueFile.id}`,
                scale: 0.6,
              }); // ローディング中ならローディング表示
            } else {
              this.$vs.loading.close(`#${valueFile.id} > .con-vs-loading`);
            }
          });
        });
      },
    },

    isNullableChecked: {
      handler() {
        this.nullableChecked = this.isNullableChecked;
      },
      immediate: true,
    },
    vValidate(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.$nextTick(async () => {
        await this.$validator.validateAll(); // nullableLabel の変更時にバリデーションを効かせる
      });
    },
    valueForValidate(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.$nextTick(async () => {
        await this.$validator.validateAll();
      });
    },
  },
  computed: {
    valueForValidate() {
      const { value } = this;
      if (!Array.isArray(value)) return undefined;
      return value[0] && value[0].name; // validationにファイル名を用いる
    },
    hasImage() {
      const { value } = this;
      return Array.isArray(value) && value.length > 0;
    },
    valueFiles() {
      if (!this.hasImage) return [];
      return this.value.map(valueFile => {
        const id = `id-${valueFile.hash}`;
        return {
          ...valueFile,
          id,
        };
      });
    },
    AISEA_COLOR() {
      return AISEA_COLOR;
    },
    eventList() {
      const { onPreview, onDownload, onDelete, isEditing } = this;
      return [
        {
          label: this.$t("Views.Operation.ImageManipulation.Preview.label"),
          icon: "EyeIcon",
          event: EVENTS.PREVIEW,
          onClick: onPreview,
        },
        {
          label: this.$t("Views.Operation.ImageManipulation.Download.label"),
          icon: "DownloadIcon",
          event: EVENTS.DOWNLOAD,
          onClick: onDownload,
        },
        {
          label: this.$t("Views.Operation.ImageManipulation.Delete.label"),
          icon: "TrashIcon",
          event: EVENTS.DELETE,
          onClick: onDelete,
        },
      ].filter(({ event }) => {
        if (event !== EVENTS.DELETE) return true;
        return isEditing; // 削除ボタンは編集中の時だけ表示
      });
    },
    images() {
      return this.valueFiles.map(({ url }) => {
        return {
          src: url,
        };
      });
    },
    imageReadErrorMessage() {
      return this.$t("Views.Operation.ImageManipulation.Read.Fail.text"); // 画像読み込みでエラー出たら表示
    },
    isNullable() {
      if (!this.hasNullableLabel) return !this.required;
      return this.nullableChecked === true;
    },
    vValidate() {
      const required = this.required && !this.isNullable ? "required|" : "";
      return `${required}`;
    },
    hasErrors() {
      return this.errors.has(this.name);
    },
    hasNullableLabel() {
      const { nullableLabel } = this;
      return isString(nullableLabel) && !isEmpty(nullableLabel);
    },
    nullableCheckBoxColor() {
      return AISEA_COLOR.BLUE;
    },
  },

  methods: {
    onPreview(image) {
      this.showPreview(); // プレビューを表示
      this.$emit(EVENTS.PREVIEW, image);
    },
    onDownload(image) {
      this.$emit(EVENTS.DOWNLOAD, image);
    },
    onDelete(image) {
      this.$emit(EVENTS.DELETE, image);
    },
    showPreview() {
      if (!this.hasImage) return;
      this.$refs.lightbox.showImage(0);
    },

    showUploadDialog() {
      this.$emit(EVENTS.SHOW_DIALOG);
    },
    closeUploadDialog() {
      this.$emit(EVENTS.CLOSE_DIALOG);
    },
    onUpload(documents) {
      this.$emit(EVENTS.UPLOAD, documents);
    },
    onChange() {
      const { disabled, value, nullableChecked } = this;
      if (disabled) return;
      this.$emit(EVENTS.CHANGE, { value, nullableChecked });
    },
    // デフォルト値でstoreを更新する
    onChangeByDefaultValue() {
      const { disabled, defaultValue, nullableChecked } = this;
      if (disabled) return;
      this.$emit(EVENTS.CHANGE, { value: defaultValue, nullableChecked });
    },
  },

  beforeDestroy() {
    this.isDefaultValueInserted = false;
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";
.operation-report-show-body-form-item {
  min-height: 90px;
  padding: 18px 0;
  row-gap: 4px;

  .image-container {
    width: 200px;
    height: 150px;
    border-radius: 6px;
    overflow: hidden;
    border-radius: 6px;
    background-color: $aisea-light-gray;
  }
  .image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .dropdown-list {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .form-item-label.is-editing {
    &.has-errors {
      color: $aisea-red; // エラー発生時に項目名を赤くする
    }

    // 必須の赤い点
    &.required:after {
      content: " *";
      font-size: 14px;
      color: $aisea-red;
      font-weight: 700;
      font-family: $aisea-font-family;
    }
  }

  // disabled時にさわれないようにする
  &.is-editing.disabled {
    .form-item-value-images,
    .image-select-button-wrapper {
      pointer-events: none;
    }
    .form-item-value-images {
      > * {
        opacity: 0.6; // 画像の表示を非活性ぽくする
      }
    }
  }

  .nullable-label {
    gap: 20px;
  }
}

.content-dropdown-menu-operation {
  width: 200px;

  ::v-deep .vs-dropdown--menu--after {
    display: none; // 吹き出しを消す
  }
}
</style>
